import PageHeader from '../components/ui/PageHeader';
import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { userFacingError } from '../utils/userFacingError';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { ArticleExtractorConfigService } from '../services/articleExtractorConfigService';
import { ArticleExtractorService, ExtractedArticle } from '../services/articleExtractorService';

const TIME_HM_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

function todayIsoDate(): string {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Konvertiert den Wert von input type="date" nach dd.MM.yyyy für den Webhook. */
function isoDateToGerman(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function htmlDownloadFilename(idmlFileName: string): string {
  const base = idmlFileName.replace(/\.idml$/i, '').trim() || 'e-book';
  return `${base}.html`;
}

function triggerHtmlDownload(html: string, filename: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const ArtikelExtraktorPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingHtml, setIsDownloadingHtml] = useState(false);
  const [articles, setArticles] = useState<ExtractedArticle[]>([]);
  const [publishingKey, setPublishingKey] = useState<string | null>(null);
  const [information, setInformation] = useState('');
  const [isSavingInformation, setIsSavingInformation] = useState(false);
  const [isLoadingInformation, setIsLoadingInformation] = useState(true);
  const [savedInformation, setSavedInformation] = useState('');
  const [informationError, setInformationError] = useState(false);
  const [reloadInformation, setReloadInformation] = useState(0);
  const [exported, setExported] = useState(false);
  const [publishedKeys, setPublishedKeys] = useState<string[]>([]);
  const confirm = useConfirmation();
  const informationDirty = information !== savedInformation;
  const localDirty = !!(file || articles.length) && !exported && !(articles.length && publishedKeys.length === articles.length);
  const [publishSettingsByKey, setPublishSettingsByKey] = useState<
    Record<string, { dateIso: string; timeHm: string }>
  >({});

  const articleKey = (article: ExtractedArticle, index: number) => `${article.order}-${index}`;

  const updatePublishSetting = (
    key: string,
    patch: Partial<{ dateIso: string; timeHm: string }>
  ) => {
    setExported(false);
    setPublishSettingsByKey((prev) => {
      const base = prev[key] ?? { dateIso: todayIsoDate(), timeHm: '18:00' };
      return {
        ...prev,
        [key]: { ...base, ...patch }
      };
    });
  };

  useEffect(() => {
    setPublishSettingsByKey((prev) => {
      const next: Record<string, { dateIso: string; timeHm: string }> = {};
      articles.forEach((article, index) => {
        const key = articleKey(article, index);
        next[key] = prev[key] ?? { dateIso: todayIsoDate(), timeHm: '18:00' };
      });
      return next;
    });
  }, [articles]);

  useEffect(() => {
    const loadInformation = async () => {
      setIsLoadingInformation(true);
      try {
        const savedInformation = await ArticleExtractorConfigService.getInformation();
        setInformation(savedInformation); setSavedInformation(savedInformation); setInformationError(false);
      } catch { setInformationError(true); } finally {
        setIsLoadingInformation(false);
      }
    };

    loadInformation();
  }, [reloadInformation]);

  const extractArticles = async (selectedFile: File) => {
    try {
      setIsSubmitting(true);
      const result = await ArticleExtractorService.extractFromIdml(selectedFile);
      setArticles(result); setPublishedKeys([]); setExported(false);
      toast.success(`${result.length} Artikel erfolgreich extrahiert.`);
    } catch (error) {
      const message = userFacingError(error, 'Unbekannter Fehler');
      toast.error(`Extraktion fehlgeschlagen: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const selectedFile = input.files?.[0];
    if (!selectedFile || isSubmitting || isDownloadingHtml || publishingKey || isSavingInformation) return;
    if (articles.length && !(await confirm({ title: 'Andere Datei auswählen?', description: 'Die aktuellen Ausleseergebnisse und Termine werden zurückgesetzt.', confirmLabel: 'Datei wechseln' }))) { input.value = ''; return; }
    setFile(selectedFile); setArticles([]); setPublishedKeys([]); setExported(false);
    setPublishSettingsByKey({});
    await extractArticles(selectedFile);
  };

  const handleSubmit = async () => {
    if (isSubmitting || isDownloadingHtml || publishingKey || isSavingInformation || !file) return;
    if (articles.length && !(await confirm({ title: 'Artikel erneut auslesen?', description: 'Die bisherigen Ergebnisse, Termine und Übergabebestätigungen werden zurückgesetzt.', confirmLabel: 'Erneut auslesen' }))) return;
    await extractArticles(file);
  };

  const handleDownloadEbookHtml = async () => {
    if (!file) {
      toast.error('Bitte zuerst eine IDML-Datei auswählen.');
      return;
    }

    try {
      setIsDownloadingHtml(true);
      const html = await ArticleExtractorService.convertIdmlToHtml(file);
      const downloadName = htmlDownloadFilename(file.name);
      triggerHtmlDownload(html, downloadName);
      setExported(true);
      toast.success(`${downloadName} wurde heruntergeladen.`);
    } catch (error) {
      const message = userFacingError(error, 'Unbekannter Fehler');
      toast.error(`HTML-Export fehlgeschlagen: ${message}`);
    } finally {
      setIsDownloadingHtml(false);
    }
  };

  const handlePublishArticle = async (article: ExtractedArticle, index: number) => {
    const key = articleKey(article, index);
    const schedule = publishSettingsByKey[key] ?? { dateIso: todayIsoDate(), timeHm: '18:00' };
    const publishDate = isoDateToGerman(schedule.dateIso);
    const publishTime = schedule.timeHm.trim();

    if (!publishDate) {
      toast.error('Bitte ein gültiges Veröffentlichungsdatum wählen.');
      return;
    }
    if (!TIME_HM_REGEX.test(publishTime)) {
      toast.error('Bitte die Uhrzeit im Format HH:MM angeben (z. B. 18:00).');
      return;
    }

    if (!(await confirm({ title: 'Artikel übergeben?', description: `„${article.titel || 'Ohne Titel'}“ wird für ${publishDate} um ${publishTime} Uhr an die Veröffentlichung übergeben.`, confirmLabel: 'Artikel übergeben' }))) return;
    try {
      setIsSavingInformation(true);
      await ArticleExtractorConfigService.saveInformation(information);
      setSavedInformation(information);
      setPublishingKey(key);
      await ArticleExtractorService.publishArticle(article, information, {
        publishDate,
        publishTime
      });
      setPublishedKeys(previous => [...previous, key]);
      toast.success(`Artikel ${article.order || index + 1} zur Veröffentlichung übergeben.`);
    } catch (error) {
      const message = userFacingError(error, 'Unbekannter Fehler');
      toast.error(`Senden fehlgeschlagen: ${message}`);
    } finally {
      setIsSavingInformation(false);
      setPublishingKey(null);
    }
  };

  const handleSaveInformation = async () => {
    if (isLoadingInformation || isSavingInformation || informationError) return;

    try {
      setIsSavingInformation(true);
      await ArticleExtractorConfigService.saveInformation(information);
      setSavedInformation(information); toast.success('Zusatztext gespeichert.');
    } catch (error) {
      const message = userFacingError(error, 'Unbekannter Fehler');
      toast.error(`Information konnte nicht gespeichert werden: ${message}`);
    } finally {
      setIsSavingInformation(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <PageHeader title="InDesign zu ef-magazin.de" description="Lade eine IDML-Datei, prüfe die ausgelesenen Artikel und wähle ihre Veröffentlichungstermine." />
        <UnsavedChangesNotice dirty={informationDirty || !!localDirty} busy={isSubmitting || isDownloadingHtml || isSavingInformation || !!publishingKey} localOnly={!informationDirty} />
      </div>

      <details className="card" open>
        <summary className="cursor-pointer font-semibold text-gray-900">Zusatztext für alle Artikel</summary>
        <div className="mt-4 space-y-2">
          {informationError && <p role="alert" className="text-sm text-red-700">Der gespeicherte Zusatztext konnte nicht geladen werden. <Button variant="outline" size="sm" onClick={() => setReloadInformation(n => n + 1)}>Erneut versuchen</Button></p>}
          <Textarea
            aria-label="Zusatztext für alle Artikel"
            value={information}
            onChange={(event) => setInformation(event.target.value)}
            placeholder={isLoadingInformation ? 'Lade gespeicherte Information...' : 'Information für alle Artikel'}
            rows={5}
            disabled={isLoadingInformation || informationError || isSavingInformation}
          />
          <p className="text-xs text-gray-500">
            Bitte für jede neue Ausgabe prüfen und aktualisieren. Dieser Text wird an jeden Artikel angehängt. Behalte die HTML-Formatierung bei.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-3"><p role="status" className="text-sm text-gray-600">{isSavingInformation ? 'Wird gespeichert …' : informationDirty ? 'Ungespeicherte Änderungen' : informationError ? 'Speicherstatus nicht verfügbar' : 'Zusatztext gespeichert'}</p><Button variant="outline" onClick={handleSaveInformation} loading={isSavingInformation} disabled={isLoadingInformation || informationError || !informationDirty}>Zusatztext speichern</Button></div>
        </div>
      </details>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
            <li>IDML aus InDesign Exportieren: Datei → Exportieren → InDesign Markup (IDML) (*.idml)</li>
            <li>IDML in e-Redaktion laden; die Artikel werden automatisch ausgelesen</li>
            <li>Veröffentlichungstermine wählen und Artikel übergeben</li>
            <li>
              Prüfe nach der Übergabe die Artikel in der{' '}
              <a
                href="https://ef-magazin.de/article/queue/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Warteschlange
              </a>
              . Es wird automatisch ein KI-Bild generiert.
            </li>
            <li>
              Notwendige manuelle Änderungen: <strong>Heftnummer</strong>,{' '}
              <strong>Automatisch kostenpflichtig nach</strong>, <strong>ggf. Artikelbild</strong>
            </li>
          </ul>
        </div>

        <div className="space-y-3">
          <label htmlFor="idml-file" className="block text-sm font-medium text-gray-700">1. IDML-Datei auswählen</label>
          <input
            type="file"
            id="idml-file"
            disabled={isSubmitting || isDownloadingHtml || !!publishingKey || isSavingInformation}
            accept=".idml"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <p className="text-sm text-gray-500">Das Auslesen startet automatisch nach der Dateiauswahl.</p>
          {file && <p className="text-sm text-gray-500">Ausgewählt: {file.name}</p>}
        </div>

        <div className="flex flex-wrap gap-3">
          {file && <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            variant="primary"
            disabled={!file || isDownloadingHtml || !!publishingKey || isSavingInformation}
          >
            {isSubmitting ? 'Artikel werden ausgelesen …' : articles.length ? 'Erneut auslesen' : 'Auslesen erneut versuchen'}
          </Button>}
          <Button
            onClick={handleDownloadEbookHtml}
            loading={isDownloadingHtml}
            variant="outline"
            disabled={!file || isSubmitting || !!publishingKey || isSavingInformation}
          >
            Als E-Book herunterladen
          </Button>
        </div>

      </div>

      <div className="card space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">2. Artikel prüfen und übergeben</h2>

        {articles.length === 0 ? (
          <p className="text-sm text-gray-500">Noch keine Artikel vorhanden.</p>
        ) : (
          <div className="space-y-4">
            {articles.map((article, index) => {
              const key = articleKey(article, index);
              const schedule = publishSettingsByKey[key] ?? { dateIso: todayIsoDate(), timeHm: '18:00' };

              return (
              <article key={key} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1 min-w-0 space-y-2">
                    <h3 className="text-base font-semibold text-gray-900">
                      Artikel {article.order || index + 1}
                    </h3>
                    <div className="space-y-1 text-sm text-gray-800">
                      <p><span className="font-semibold">Autor:</span> {article.autor || '-'}</p>
                      <p><span className="font-semibold">Thema:</span> {article.thema || '-'}</p>
                      <p><span className="font-semibold">Titel:</span> {article.titel || '-'}</p>
                      <p><span className="font-semibold">Untertitel:</span> {article.untertitel || '-'}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[220px] shrink-0">
                    <Input
                      label="Veröffentlichungsdatum"
                      type="date"
                      value={schedule.dateIso}
                      onChange={(event) =>
                        updatePublishSetting(key, { dateIso: event.target.value })}
                      disabled={isLoadingInformation || !!publishingKey || publishedKeys.includes(key) || isSavingInformation}
                      className="w-full sm:max-w-xs"
                    />
                    <Input
                      label="Veröffentlichungsuhrzeit"
                      type="text"
                      value={schedule.timeHm}
                      onChange={(event) =>
                        updatePublishSetting(key, { timeHm: event.target.value })}
                      placeholder="HH:MM"
                      pattern={TIME_HM_REGEX.source}
                      inputMode="numeric"
                      disabled={isLoadingInformation || !!publishingKey || publishedKeys.includes(key) || isSavingInformation}
                      helperText="Format: HH:MM (z. B. 18:00)"
                      className="w-full sm:max-w-xs"
                    />
                    <Button
                      className="w-full sm:w-auto shrink-0 sm:self-end"
                      onClick={() => handlePublishArticle(article, index)}
                      loading={publishingKey === key || isSavingInformation}
                      variant="primary"
                      size="sm"
                      disabled={isLoadingInformation || informationError || isSavingInformation || !!publishingKey || publishedKeys.includes(key)}
                    >
                      {publishedKeys.includes(key) ? 'Übergabe bestätigt' : 'Artikel übergeben'}
                    </Button>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">Text</p>
                  {article.htmlBody ? (
                    <div
                      className="text-sm text-gray-800 leading-6 bg-white border border-gray-200 rounded-md p-3 [&>p]:mb-4 [&>p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: article.htmlBody }}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-sm text-gray-800 leading-6 bg-white border border-gray-200 rounded-md p-3">
                      {article.body || '-'}
                    </div>
                  )}
                </div>
              </article>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtikelExtraktorPage;
